import { fullName, isEmpty, isNotEmpty, isNotEmptyArray, isValidUUID } from '@/pipes';
import { AccessControlledModel } from '../AccessControlledModel';
import { ScoutingReportPlayerDetails, ScoutingReportGameDetails, ScoutingReportEvaluationMatrix, ContactInfo, ScoutingReportEvaluationMatrixNotes, ScoutingReportScore } from '@best-athletes/ba-types';
import { PartialAthleteProfile, PartialCoachProfile, PartialTeam, PlayerOnTeam, ratingToScore, TeamJersey } from '@/models';
import { PartialOrganization } from '@/models/organization/OrganizationModel';
import { SportName, WebLink } from '@/../types';
import { SportModel } from '@/models/sport';
import { ObjectCache } from '@/helpers';
import { athleteApi } from '@/api/AthleteApi';
import { coachApi } from '@/api/CoachApi';
import { teamApi } from '@/api/TeamApi';
import { organizationApi } from '@/api/OrganizationApi';
import { sportApi } from '@/api/SportApi';
import { AthleteReportCategory } from '@/models';

export class ScoutingReportEvaluation {
	athleteAttributes: Array<AthleteReportCategory>;
	gameMoments: Array<AthleteReportCategory>;
	scoutingEval: Array<AthleteReportCategory>;
	performance: AthleteReportCategory;
	potential: AthleteReportCategory;

	constructor() {
		this.performance = new AthleteReportCategory('Performance');
		this.potential = new AthleteReportCategory('Potential');
	}
}

export class ScoutingReportModel extends AccessControlledModel {
	version: number = 3;
	createdAt: Date = new Date();
	athleteId?: string | null = null;
	coachId: string = undefined;
	contactInfo: ContactInfo | null = null;
	lastModified: Date | null = null;
	anonymousCoach: boolean = false;

	playerDetails: ScoutingReportPlayerDetails;
	gameDetails: ScoutingReportGameDetails;
	evaluationMatrix: ScoutingReportEvaluationMatrix;					// legacy. deprecated
	evaluationMatrixNotes: ScoutingReportEvaluationMatrixNotes;			// legacy. deprecated

	evaluation: ScoutingReportEvaluation;
	tags: string = "";

	weblinks?: Array<WebLink>;

	submitted: boolean = false;

	constructor() {
		super();
		this.createdAt = new Date;
		this.athleteId = undefined;
		this.contactInfo = {
			firstName: '',
			lastName: '',
			email: '',
			lastContact: new Date,
		};
		this.lastModified = new Date;
		this.anonymousCoach = false;
		this.playerDetails = {
			firstName: '',
			lastName: '',
			position: '',
		};
		this.gameDetails = {
			coachId: '',
			evaluationDate: new Date,
		}
		this.evaluation = new ScoutingReportEvaluation;
		this.evaluationMatrix = {
			technical: null,
			tactical: null,
			physical: null,
			mental: null,
			attackingOrganization: null,
			attackingTransition: null,
			attackingSetPlays: null,
			defendingOrganization: null,
			defendingTransition: null,
			defendingSetPlays: null,
			competitive: null,
			resilience: null,
			intelligence: null,
			speed: null,
			presence: null,
			performanceScore: null,
			potentialScore: null,
		};
		this.evaluationMatrixNotes = {
			technical: '',
			tactical: '',
			physical: '',
			mental: '',
			attackingOrganization: '',
			attackingTransition: '',
			attackingSetPlays: '',
			defendingOrganization: '',
			defendingTransition: '',
			defendingSetPlays: '',
			competitive: '',
			resilience: '',
			intelligence: '',
			speed: '',
			presence: '',
			performanceScore: '',
			potentialScore: '',
		};
		this.submitted = false;
	}
	
	/** True if all steps of the Evaluation Matrix have been given a grade */
	get IsComplete(): boolean{
		return this.submitted;
	}

	get PlayerName(): string | null {
		return fullName(this.playerDetails.firstName, this.playerDetails.lastName);
	}

	get AthleteId(): string {
		return this.athleteId;
	}
	get CoachId(): string {
		if( isNotEmpty( this.gameDetails?.coachId ) ) return this.gameDetails.coachId;
		return this.coachId;
	}

	get HasContactInfo(): boolean{
		if(this.contactInfo === null) return false;
		return this.contactInfo.email !== "";
	}

	EvaluationStr(item: string): string {
		return 'to-do'
	}

	load(obj: Record<string, any>): this {
		Object.assign(this, obj);
		if(obj['createdAt']) this.createdAt = new Date(obj['createdAt']);
		if (obj['lastModified'] !== null) this.lastModified = new Date(obj['lastModified']);
		if (obj['playerDetails'] && obj['playerDetails']['dateOfBirth']){
			this.playerDetails.dateOfBirth = new Date(obj['playerDetails']['dateOfBirth']);
		}
		if (obj['gameDetails'] && obj['gameDetails']['evaluationDate']){
			this.gameDetails.evaluationDate = new Date(obj['gameDetails']['evaluationDate']);
		}

		if( isEmpty(obj.evaluationMatrixNotes) ) {
			this.evaluationMatrixNotes = {
				technical: null,
				tactical: null,
				physical: null,
				mental: null,
				attackingOrganization: null,
				attackingTransition: null,
				attackingSetPlays: null,
				defendingOrganization: null,
				defendingTransition: null,
				defendingSetPlays: null,
				competitive: null,
				resilience: null,
				intelligence: null,
				speed: null,
				presence: null,
				performanceScore: null,
				potentialScore: null,
			}
		}
		if( isNotEmpty(obj.playerDetails) ) {
			if (obj['playerDetails']['competitiveLevel'] == undefined) {
				this.playerDetails.competitiveLevel = null;
			}
		}
		if( isEmpty(this.evaluation) ) this.evaluation = new ScoutingReportEvaluation;

		// convert legacy deprecated 'evaluationMatrix' into 'evaluation'
		if( isNotEmpty(obj.evaluationMatrix) ) {
			if( isEmpty(this.evaluation.athleteAttributes) ) {
				this.evaluation.athleteAttributes = [
					new AthleteReportCategory("Technical", obj.evaluationMatrix.technical, obj.evaluationMatrixNotes.technical),
					new AthleteReportCategory("Tactical", obj.evaluationMatrix.tactical, obj.evaluationMatrixNotes.tactical),
					new AthleteReportCategory("Physical", obj.evaluationMatrix.physical, obj.evaluationMatrixNotes.physical),
					new AthleteReportCategory("Mental", obj.evaluationMatrix.mental, obj.evaluationMatrixNotes.mental),
				];
			}
			if( isEmpty(this.evaluation.gameMoments) ) {
				this.evaluation.gameMoments = [
					new AthleteReportCategory("Attacking Organization", obj.evaluationMatrix.attackingOrganization, obj.evaluationMatrixNotes.attackingOrganization),
					new AthleteReportCategory("Attacking Transition", obj.evaluationMatrix.attackingTransition, obj.evaluationMatrixNotes.attackingTransition),
					new AthleteReportCategory("Attacking Set Plays", obj.evaluationMatrix.attackingSetPlays, obj.evaluationMatrixNotes.attackingSetPlays),
					new AthleteReportCategory("Defending Organization", obj.evaluationMatrix.defendingOrganization, obj.evaluationMatrixNotes.defendingOrganization),
					new AthleteReportCategory("Defending Transition", obj.evaluationMatrix.defendingTransition, obj.evaluationMatrixNotes.defendingTransition),
					new AthleteReportCategory("Defending Set Plays", obj.evaluationMatrix.defendingSetPlays, obj.evaluationMatrixNotes.defendingSetPlays),
				];
			}
			if( isEmpty(this.evaluation.scoutingEval) ) {
				this.evaluation.scoutingEval = [
					new AthleteReportCategory("Competitive", obj.evaluationMatrix.competitive, obj.evaluationMatrixNotes.competitive),
					new AthleteReportCategory("Resilience", obj.evaluationMatrix.resilience, obj.evaluationMatrixNotes.resilience),
					new AthleteReportCategory("Intelligence", obj.evaluationMatrix.intelligence, obj.evaluationMatrixNotes.intelligence),
					new AthleteReportCategory("Speed", obj.evaluationMatrix.speed, obj.evaluationMatrixNotes.speed),
					new AthleteReportCategory("Presence", obj.evaluationMatrix.presence, obj.evaluationMatrixNotes.presence),
				];
			}
			if( isEmpty(this.evaluation.performance) ) this.evaluation.performance = new AthleteReportCategory('Performance', obj.evaluationMatrix.performanceScore, obj.evaluationMatrixNotes.performanceScore);
			if( isEmpty(this.evaluation.potential) ) this.evaluation.potential = new AthleteReportCategory('Potential', obj.evaluationMatrix.potentialScore, obj.evaluationMatrixNotes.potentialScore);
			if( isEmpty(this.evaluation.performance.rating) ) this.evaluation.performance.rating = obj.evaluationMatrix.performanceScore;
			if( isEmpty(this.evaluation.performance.notes) ) this.evaluation.performance.notes = obj.evaluationMatrixNotes.performanceScore;
			if( isEmpty(this.evaluation.performance.score) ) this.evaluation.performance.score = ratingToScore(this.evaluation.performance.rating);
			if( isEmpty(this.evaluation.potential.rating) ) this.evaluation.potential.rating = obj.evaluationMatrix.potentialScore;
			if( isEmpty(this.evaluation.potential.notes) ) this.evaluation.potential.notes = obj.evaluationMatrixNotes.potentialScore;
			if( isEmpty(this.evaluation.potential.score) ) this.evaluation.potential.score = ratingToScore(this.evaluation.potential.rating);
			this.ComputePerformanceScore();
		}

		return this;
	}

	get AllEvaluations(): Array<AthleteReportCategory> {
		if( isEmpty(this.evaluation) ) return [];
		if( isEmpty(this.evaluation.athleteAttributes) ) this.evaluation.athleteAttributes = [];
		if( isEmpty(this.evaluation.gameMoments) ) this.evaluation.gameMoments = [];
		if( isEmpty(this.evaluation.scoutingEval) ) this.evaluation.scoutingEval = [];
		const allEvals: Array<AthleteReportCategory> = this.evaluation.athleteAttributes.concat(this.evaluation.gameMoments, this.evaluation.scoutingEval);
		return allEvals.filter(e => e.score > 0);
	}
	ComputePerformanceScore() {
		const categories = this.AllEvaluations.filter(s => isNotEmpty(s.score));
		const scores = categories.map(s => Number(s.score));
		this.evaluation.performance.score = (scores.length > 0)? scores.reduce((a,b) => a+b) / scores.length : undefined;
	}
	get HasComments(): boolean {
		const categoryWithComments = this.AllEvaluations.find(category => isNotEmpty(category.notes))
		return isNotEmpty(categoryWithComments);
	}

	EvaluationCategory(section: string, category: string): AthleteReportCategory {
		if( isEmpty(this.evaluation) ) return undefined;
		if( isEmpty(this.evaluation[section]) ) return undefined;
		return this.evaluation[section].find(c => c.name.toLowerCase().trim() === category.toLowerCase().trim() );
	}
}

export class ScoutingReportModelEx extends ScoutingReportModel {
	athlete: PartialAthleteProfile;
	coach: PartialCoachProfile;
	team: PartialTeam;
	player: PlayerOnTeam;
	org: PartialOrganization;
	sport: SportModel;
	
	async loadEx( athletesCache: ObjectCache<PartialAthleteProfile>,
		coachesCache: ObjectCache<PartialCoachProfile>,
		teamsCache: ObjectCache<PartialTeam>,
		orgsCache: ObjectCache<PartialOrganization>,
		sportsCache: ObjectCache<SportModel>
	) {
		await this.loadAthlete(athletesCache);
		await this.loadCoach(coachesCache);
		await this.loadTeam(teamsCache);
		await this.loadPlayer();
		await this.loadOrganization(orgsCache);
		await this.loadSport(sportsCache);
		await this.loadEvaluation();
		await this.ComputePerformanceScore();
	}

	async fromReport(report: Partial<ScoutingReportModel>,
		athletesCache: ObjectCache<PartialAthleteProfile>,
		coachesCache: ObjectCache<PartialCoachProfile>,
		teamsCache: ObjectCache<PartialTeam>,
		orgsCache: ObjectCache<PartialOrganization>,
		sportsCache: ObjectCache<SportModel>
	) {
		Object.assign(this, report);
		await this.loadEx(athletesCache, coachesCache, teamsCache, orgsCache, sportsCache);
	}
	async fromIds(athleteId: string, coachId: string, teamId: string,
		athletesCache: ObjectCache<PartialAthleteProfile>,
		coachesCache: ObjectCache<PartialCoachProfile>,
		teamsCache: ObjectCache<PartialTeam>,
		orgsCache: ObjectCache<PartialOrganization>,
		sportsCache: ObjectCache<SportModel>
	) {
		this.athleteId = athleteId;
		this.coachId = coachId;

		this.gameDetails.coachId = coachId;
		this.gameDetails.teamName = teamId;
		
		await this.loadEx(athletesCache, coachesCache, teamsCache, orgsCache, sportsCache);

		this.contactInfo.firstName = this.athlete?.firstName;
		this.contactInfo.lastName = this.athlete?.lastName;
		this.contactInfo.email = this.athlete?.Email;
		this.playerDetails.firstName = this.HasPlayer? this.player.firstName : this.athlete?.firstName;
		this.playerDetails.lastName = this.HasPlayer? this.player.lastName : this.athlete?.lastName;
		this.playerDetails.position = this.HasPlayer? this.player.position : this.athlete?.primaryPosition;
		this.playerDetails.dateOfBirth = this.HasAthlete? new Date(this.athlete.birthDate) : undefined;
		this.playerDetails.yearOfBirth = this.athlete?.birthDate?.getFullYear();
		this.playerDetails.dominantFoot = this.athlete?.dominantSide;
		this.playerDetails.jerseyNumber = Number(this.HasPlayer? this.player.number : this.athlete?.PlayerNumber);
		this.gameDetails.teamName = this.HasTeam? this.team.name : teamId;
	}

	async loadAthlete(athletesCache: ObjectCache<PartialAthleteProfile>) {
		if( isEmpty(this.AthleteId) ) return;

		this.athlete = athletesCache.get(this.AthleteId);
		if( isNotEmpty(this.athlete) ) return;

		this.athlete = await athleteApi.getPublicProfile(this.AthleteId);
		athletesCache.set(this.athlete);
	}

	async loadCoach(coachesCache: ObjectCache<PartialCoachProfile>) {
		if( isEmpty(this.CoachId) ) return;

		this.coach = coachesCache.get(this.CoachId);
		if( isNotEmpty(this.coach) ) return;

		this.coach = await coachApi.findById(this.CoachId);
		coachesCache.set(this.coach);
	}

	async fetchTeam(teamId: string, teamsCache: ObjectCache<PartialTeam>) {
		if( isEmpty(teamId) ) return;

		if( isValidUUID(teamId) ) this.team = teamsCache.get(teamId);
		if( isNotEmpty(this.team) ) return;

		// if teamId is a valid teamId, find team in database
		if( isValidUUID(teamId) ) this.team = await teamApi.findById(teamId);
		// if teamId is a string, find team by name
		else this.team = await teamApi.findByName(teamId);
		teamsCache.set(this.team);
	}
	async loadTeam(teamsCache: ObjectCache<PartialTeam>) {
		if( !this.athlete ) return;

		if( isNotEmpty(this.gameDetails?.teamName) ) {
			// athlete teams are loaded, find the team from loaded by team name
			if( isNotEmpty(this.athlete.teams) ) this.team = this.athlete.teams.find(t => t.name === this.gameDetails.teamName );
			
			// use the report team name, which can be a teamId or a team name
			await this.fetchTeam(this.gameDetails.teamName, teamsCache);
			if( isNotEmpty(this.team) ) return;

			// team not found in database
			return;
		}
		
		// find the athlete's current team
		if( isValidUUID(this.athlete.currentTeam) ) {
			await this.fetchTeam(this.athlete.currentTeam, teamsCache);
			if( isNotEmpty(this.team) ) return;
		}

		// if athlete teams are loaded, use the athlete's latest team
		if( isNotEmpty(this.athlete.teams) ) {
			this.team = this.athlete.teams.slice(-1)[0];
			if( isNotEmpty(this.team) ) {
				teamsCache.set(this.team);
				return;
			}
		}

		// find athlete teams and use the latest team
		const teams = await teamApi.findByAthleteId(this.athlete.id);
		if( isNotEmpty(teams) ) {
			this.team = teams.slice(-1)[0];
			teamsCache.setMultiple(teams);
		}
	}
	async loadPlayer() {
		if( !this.HasTeam ) return;
		this.player = this.team.players.find(p => p.athleteId === this.AthleteId);
	}
	async loadOrganization(orgsCache: ObjectCache<PartialOrganization>) {
		if( !this.HasTeam ) return;

		let orgId: string = this.team.organizationId;
		if( isEmpty(orgId) && isNotEmptyArray(this.team.organizations) ) orgId = this.team.organizations[0].id;
		if( isEmpty(orgId) ) return;

		this.org = orgsCache.get(orgId);
		if( isNotEmpty(this.org) ) return;

		this.org = await organizationApi.publicFindById(orgId);
		orgsCache.set(this.org);
	}
	async loadSport(sportsCache: ObjectCache<SportModel>) {
		let sportName: string = this.org?.sport;
		if( isEmpty(sportName) ) sportName = this.team?.Sport;
		if( isEmpty(sportName) ) sportName = this.coach?.SportName;
		if( isEmpty(sportName) ) sportName = this.athlete?.Sport?.name;
		if( isEmpty(sportName) ) sportName = SportName.Soccer;
		if( isEmpty(sportName) ) return;

		this.sport = sportsCache.get(sportName);
		if( isNotEmpty(this.sport) ) return;

		this.sport = await sportApi.findByName(sportName);
		sportsCache.set(this.sport);
	}
	async loadEvaluation() {
		if( this.HasEvaluation ) return;

		// CCM_TODO - think about the order below
		if( this.HasOrg ) {
			this.evaluation.athleteAttributes = this.org.scoutingReportLabels.map(l => new AthleteReportCategory(l));
			this.evaluation.gameMoments = this.org.scoutingReportMoments.map(m => new AthleteReportCategory(m));
			this.evaluation.scoutingEval = this.org.scoutingReportEvaluations.map(e => new AthleteReportCategory(e));
		} 
		if( (this.AllEvaluations.length === 0) && this.HasSport ) {
			this.evaluation.athleteAttributes = this.sport.scoutingLabels.map(l => new AthleteReportCategory(l));
			this.evaluation.gameMoments = this.sport.scoutingMoments.map(m => new AthleteReportCategory(m));
			this.evaluation.scoutingEval = this.sport.scoutingEvaluations.map(e => new AthleteReportCategory(e));
		}

		// absolute defaults
		if( isEmpty(this.evaluation.athleteAttributes) ) this.evaluation.athleteAttributes = [new AthleteReportCategory('technical'), new AthleteReportCategory('physical')];
		if( isEmpty(this.evaluation.gameMoments) ) this.evaluation.gameMoments = [new AthleteReportCategory('attack'), new AthleteReportCategory('defend')];
		if( isEmpty(this.evaluation.scoutingEval) ) this.evaluation.scoutingEval = [new AthleteReportCategory('mental')];
	}

	get HasAthlete(): boolean {
		return isNotEmpty(this.athlete);
	}
	get HasCoach(): boolean {
		return isNotEmpty(this.coach);
	}
	get HasTeam(): boolean {
		return isNotEmpty(this.team);
	}
	get HasPlayer(): boolean {
		return isNotEmpty(this.player);
	}
	get HasOrg(): boolean {
		return isNotEmpty(this.org);
	}
	get HasSport(): boolean {
		return isNotEmpty(this.sport);
	}
	get HasEvaluation(): boolean {
		return isNotEmpty(this.evaluation?.athleteAttributes) || isNotEmpty(this.evaluation?.gameMoments) || isNotEmpty(this.evaluation?.scoutingEval);
	}
	get AthleteName(): string {
		if( isEmpty(this.athlete) ) return this.PlayerName;
		return this.athlete.FullName;
	}
	get CoachName(): string {
		if( isEmpty(this.coach) ) return this.CoachId;
		return this.coach.FullName;
	}
	get TeamName(): string {
		if( isEmpty(this.team) ) return this.gameDetails.teamName;
		return this.team.name;
	}
	get JerseyNumber(): number {
		if( isNotEmpty(this.player?.number) ) return Number(this.player.number);
		return this.playerDetails.jerseyNumber;
	}
	get Jersey(): TeamJersey {
		return this.team?.jerseyPrimary;
	}
}